import { Card, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { FileInfo, FileInfoInput } from '../../../../../../generated';
import FileSelectionCard from '../../../../../shared/file-selection';
import { FilePicker } from './file-picker';

interface VerificationStatusReportDocumentPickerProps {
  onMergePdfs: (blobPaths: FileInfoInput[]) => Promise<void>;
  blobUrls: FileInfo[];
  verificationFileNames: Map<string, string>;
  isProcessingMergingPdfs: boolean;
  onCancel: () => void;
}

export interface SelectedFileInfo {
  name: string;
  url: string;
  type: string;
  caseType: string;
  caseId: string;
}

export interface SelectedFilesMap {
  [key: string]: SelectedFileInfo | null;
}

export const VerificationStatusReportDocumentPicker: React.FC<VerificationStatusReportDocumentPickerProps> = (props) => {
  const initialFiles = props.blobUrls.reduce((acc, blob) => {
    const key =
      blob.name?.includes('cover-letter') ? 'cover-letter' :
      blob.name?.includes('verification-component-packet') ? 'verification-component-packet' :
      '';
  
    if (key) {
      acc[key] = {
        name: blob.name || '',
        url: blob.url || '',
        type: 'application/pdf',
        caseType: '',
        caseId: ''
      };
    }
  
    return acc;
  }, {} as SelectedFilesMap);
  

  function useFileSelection(initialFiles: SelectedFilesMap) {
    const [selectedFilesMap, setSelectedFilesMap] = useState<SelectedFilesMap>(initialFiles);
    const [currentFileSelection, setCurrentFileSelection] = useState<string>('');

    const handleFileSelection = (fileInfo: SelectedFileInfo) => {
      setSelectedFilesMap((prevFilesMap) => ({
        ...prevFilesMap,
        [currentFileSelectionRef.current]: { ...fileInfo,  type: fileInfo.type || 'application/pdf'}
      }));
    };

    const handleCancel = () => {
      props.onCancel();
      setCurrentFileSelection('');
      setSelectedFilesMap(initialFiles);
    };

    return {
      selectedFilesMap,
      currentFileSelection,
      setCurrentFileSelection,
      handleFileSelection,
      onCancel: handleCancel
    };
  }

  const { selectedFilesMap, currentFileSelection, setCurrentFileSelection, handleFileSelection, onCancel: handleCancel } = useFileSelection(initialFiles);

  const handleGenerateDocument = async () => {
    const requiredFiles = ['cover-letter', 'verification-component-packet'];
    const allFilesPresent = requiredFiles.every((file) => selectedFilesMap[file]);
    if (!allFilesPresent) {
      message.error('Please select all required files');
      return;
    }
    const blobPaths: FileInfoInput[] = [];
    for (const key in selectedFilesMap) {
      const fileInfo = selectedFilesMap[key];
      if (fileInfo) {
        const blobInfo: FileInfoInput = {
          fileName: fileInfo.name,
          fileType : fileInfo.type
        }
        blobPaths.push(blobInfo);
      }
    }
    await props.onMergePdfs(blobPaths);
  };

  const files = [
    {
      type: 'cover-letter',
      label: 'Select',
      fileName: props.verificationFileNames.get('coverLetter') ?? 'cover-letter.pdf',
      isRequired: true
    },
    {
      type: 'verification-component-packet',
      label: 'Select',
      fileName: props.verificationFileNames.get('verificationComponentPacket') ?? 'verification-component-packet.pdf',
      isRequired: true
    },
  ];

  const currentFileSelectionRef = useRef(currentFileSelection);
  useEffect(() => {
    currentFileSelectionRef.current = currentFileSelection;
  }, [currentFileSelection]);

  const currentFilePicker = `File Picker - ${currentFileSelection}`;
  return (
    <div
      style={{
        display: 'flex'
      }}
    >
      <FileSelectionCard
        title="Generate Verification Status Report"
        files={files}
        selectedFilesMap={selectedFilesMap}
        onFileSelect={setCurrentFileSelection}
        currentFileSelection={currentFileSelection}
        onCancel={handleCancel}
        onGenerateDocument={handleGenerateDocument}
        isProcessingMergingPdfs={props.isProcessingMergingPdfs}
      />
      {currentFileSelection !== '' && (
        <Card title={currentFilePicker}>
          <FilePicker handleFileSelection={handleFileSelection} />
        </Card>
      )}
    </div>
  );
};
